import {
  AUDIT_ACTION_LABELS,
  type AuditLog,
  type Branch,
  type Page as PageDto,
} from '@edms/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ScrollText, Search } from 'lucide-react';
import { useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { type Column, DataTable, Pagination } from '@/components/ui/data-table';
import { Input, Select } from '@/components/ui/primitives';
import { Badge, EmptyState } from '@/components/ui/surfaces';
import { api } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';

/**
 * Journal d'audit (§13).
 *
 * Consultation seule, sans exception : le journal n'est modifiable par personne
 * depuis l'application, ce qui est la condition pour qu'il ait valeur de preuve.
 * Chaque ligne porte l'utilisateur, la date, l'heure, l'adresse IP, le
 * navigateur et l'action — la liste exacte exigée par le cahier des charges.
 */
export function AuditPage(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [branchId, setBranchId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [page, setPage] = useState(1);

  const { data: branches } = useQuery({
    queryKey: ['branches', 'selectable'],
    queryFn: () => api.get<Branch[]>('/branches/selectable'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['audit', { search, action, branchId, dateFrom, page }],
    queryFn: () =>
      api.get<PageDto<AuditLog>>('/audit-logs', {
        page,
        pageSize: 50,
        search: search || undefined,
        action: action || undefined,
        branchId: branchId || undefined,
        dateFrom: dateFrom || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const columns: readonly Column<AuditLog>[] = [
    {
      key: 'occurredAt',
      header: 'Horodatage',
      width: '14%',
      render: (row) => (
        <span className="tabular text-xs text-ink-muted">{formatDateTime(row.occurredAt)}</span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '18%',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge tone={row.outcome === 'FAILURE' ? 'danger' : 'neutral'}>{row.actionLabel}</Badge>
        </div>
      ),
    },
    {
      key: 'actor',
      header: 'Utilisateur',
      width: '18%',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-ink">{row.actorName ?? '—'}</span>
          <span className="text-2xs text-ink-faint">{row.actorEmail ?? 'non authentifié'}</span>
        </div>
      ),
    },
    {
      key: 'branch',
      header: 'Bureau',
      width: '9%',
      render: (row) => (
        <span className="font-mono text-2xs text-ink-faint">{row.branchCode ?? '—'}</span>
      ),
    },
    {
      key: 'entity',
      header: 'Objet',
      width: '14%',
      hideOnNarrow: true,
      render: (row) =>
        row.entityType ? (
          <div className="flex flex-col">
            <span className="text-xs text-ink-muted">{row.entityType}</span>
            <span className="truncate font-mono text-2xs text-ink-faint">
              {row.entityId?.slice(0, 12) ?? ''}
            </span>
          </div>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        ),
    },
    {
      key: 'ip',
      header: 'Adresse IP',
      width: '11%',
      hideOnNarrow: true,
      render: (row) => <span className="font-mono text-2xs text-ink-faint">{row.ipAddress ?? '—'}</span>,
    },
    {
      key: 'agent',
      header: 'Navigateur',
      hideOnNarrow: true,
      render: (row) => (
        <span className="block max-w-64 truncate text-2xs text-ink-faint" title={row.userAgent ?? ''}>
          {summarizeUserAgent(row.userAgent)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Journal d'audit"
        description="Toutes les actions sont enregistrées : connexion, consultation, correction, archivage"
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              size="sm"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Utilisateur, e-mail ou action…"
              leadingIcon={<Search className="size-4" />}
              className="w-72"
              aria-label="Rechercher dans le journal"
            />

            <Select
              size="sm"
              value={action}
              onChange={(event) => {
                setAction(event.target.value);
                setPage(1);
              }}
              className="w-56"
              aria-label="Filtrer par action"
            >
              <option value="">Toutes les actions</option>
              {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>

            <Select
              size="sm"
              value={branchId}
              onChange={(event) => {
                setBranchId(event.target.value);
                setPage(1);
              }}
              className="w-52"
              aria-label="Filtrer par bureau"
            >
              <option value="">Tous les bureaux</option>
              {branches?.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                </option>
              ))}
            </Select>

            <Input
              size="sm"
              type="date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                setPage(1);
              }}
              className="w-44"
              aria-label="À partir du"
            />
          </div>
        }
      />

      <PageBody>
        <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            empty={
              <EmptyState
                icon={ScrollText}
                title="Aucune entrée"
                description="Aucune action ne correspond aux critères sélectionnés."
              />
            }
          />
          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </PageBody>
    </>
  );
}

/** Réduit une chaîne User-Agent à ce qu'un auditeur lit réellement. */
function summarizeUserAgent(userAgent: string | null): string {
  if (!userAgent) return '—';

  const browser =
    /Edg\/[\d.]+/.exec(userAgent)?.[0] ??
    /Chrome\/[\d.]+/.exec(userAgent)?.[0] ??
    /Firefox\/[\d.]+/.exec(userAgent)?.[0] ??
    /Safari\/[\d.]+/.exec(userAgent)?.[0];

  const platform = /Windows NT [\d.]+|Mac OS X [\d_]+|Linux|Android|iPhone/.exec(userAgent)?.[0];

  return [browser, platform].filter(Boolean).join(' · ') || userAgent.slice(0, 48);
}
